import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionHandler,
  Create,
  EmptyStation,
  MessageBar,
  type Nullable,
  type ObjectSchemaDefinition,
  SelectField,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field } from 'formik';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  CreateIdpUserStoreDocument,
  type CreateIdpUserStoreMutation,
  type CreateIdpUserStoreMutationVariables,
  UserStoreCategory,
} from '../../../../generated/axauth-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import { getUserStoreCategoryText } from '../UserStores/UserStores';

type FormData = Nullable<
  CreateIdpUserStoreMutationVariables['input']['idpUserStore']
>;

export const userStoreCreateSchema = Yup.object<
  ObjectSchemaDefinition<FormData>
>({
  name: Yup.string().label('Name').required().max(200),
  category: Yup.string()
    .label('Category')
    .required()
    .default(UserStoreCategory.Management),
});

type SubmitResponse = Nullable<
  CreateIdpUserStoreMutation['createIdpUserStore']
>;

export const UserStoreCreate: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  // Check the status of axauth-service
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.AXAUTH_SERVICE,
          envId,
        );
        setServiceStatusMessage(statusMessage);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId]);

  const initialFormValues: FormData = {
    name: '',
    category: Object.values(UserStoreCategory)[0],
  };

  const saveData = useCallback(
    async (formData: FormData): Promise<SubmitResponse> => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
      );

      const response = (
        await envApolloClient.mutate<
          CreateIdpUserStoreMutation,
          CreateIdpUserStoreMutationVariables
        >({
          mutation: CreateIdpUserStoreDocument,
          variables: {
            input: {
              idpUserStore: {
                name: formData.name ?? '',
                category: formData.category,
              },
            },
          },
          fetchPolicy: 'no-cache',
        })
      ).data?.createIdpUserStore;
      return response ?? null;
    },
    [envId],
  );

  const history = useHistory();
  const onProceed = useCallback<ActionHandler<FormData, SubmitResponse>>(
    ({ submitResponse }) => {
      const userStore = submitResponse?.idpUserStore;
      if (userStore) {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStore.id}`,
        );
      } else {
        // The schema has the response.data properties marked as optional, since theoretically a user could have
        // permissions to mutate but not to read. In practice this can not happen on that service, so we just throw
        // an error in case we get there.
        throw new Error('Not expected');
      }
    },
    [envId, history],
  );

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="New User Store">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Create<FormData, SubmitResponse>
          title="New User Store"
          validationSchema={userStoreCreateSchema}
          saveData={saveData}
          onProceed={onProceed}
          cancelNavigationUrl={`/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores`}
          initialData={{
            data: initialFormValues,
            loading,
          }}
        >
          <Form />
        </Create>
      )}
    </>
  );
};

const Form: React.FC = () => {
  return (
    <>
      <Field name="name" label="Name" as={SingleLineTextField} />
      <Field
        name="category"
        label="Category"
        as={SelectField}
        options={Object.values(UserStoreCategory).map((category) => ({
          value: category,
          label: getUserStoreCategoryText(category),
        }))}
      />
    </>
  );
};

export const UserStoreCreateCrumb: BreadcrumbResolver = () => 'New User Store';
